import type {
  DatePickerMode,
  DatePickerValues,
  DatepickerProps,
} from '@latitude/datepicker';
import { Datepicker } from '@latitude/datepicker';
import { Tooltip } from '@latitude/tooltip';
import { useState } from 'react';
import type { ToggleGroupDatepickerItemComponentProps } from 'types';
import { useDeepCompareMemo } from 'use-deep-compare';

/**
 * ToggleGroupItemButton component renders a toggle button.
 * @param {ToggleGroupItemDatepickerProps} props - The props for the component.
 * @returns {JSX.Element} The ToggleGroupItemButton component.
 */
export const ToggleGroupItemDatepicker = <T,>({
  selected,
  item,
  analytics,
  componentTriggerType: _componentTriggerType,
  Component,
  ...rest
}: ToggleGroupDatepickerItemComponentProps<T>) => {
  const [tooltipContent, setTooltipContent] = useState<string>();

  const {
    id,
    label,
    disabled = false,
    mode,
    datepickerProps = {},
    tooltipLabelFormatter = (value) => value.label,
    selectedValueFormatter = (value) => value.value,
    onRemove,
    defaultTooltip,
    icon,
  } = item;

  const parsedMode: DatePickerMode = mode || 'single';
  const [open, setOpen] = useState(false);

  const itemComponent = useDeepCompareMemo(() => {
    const onDayPick = (newVal: DatePickerValues<DatePickerMode>) => {
      const { onClick, ...rest } = item;
      const newItem = {
        ...rest,
        value: selectedValueFormatter({ ...item, value: newVal.value }),
      };

      setTooltipContent(
        tooltipLabelFormatter
          ? tooltipLabelFormatter(newItem)
          : newVal.formattedValue,
      );

      analytics('toggle_selected_item', newItem);
      if ('onClick' in item) {
        onClick && onClick(newItem);
      }
    };

    return (
      <Datepicker
        {...({
          open,
          setOpen,
          mode: parsedMode,
          value: selected?.value,
          onChange: (newDate: DatePickerValues<DatePickerMode>) => {
            onDayPick(newDate);
          },
          ...datepickerProps,
        } as DatepickerProps)}
      >
        <Component
          {...rest}
          aria-pressed={!!selected}
          disabled={disabled}
          selected={!!selected}
          onRemove={onRemove}
        >
          {(selected && tooltipContent) || label}
          {icon && <span className="ml-1">{icon}</span>}
        </Component>
      </Datepicker>
    );
  }, [
    Component,
    datepickerProps,
    disabled,
    label,
    onRemove,
    open,
    parsedMode,
    rest,
    selected,
    tooltipContent,
    analytics,
    tooltipLabelFormatter,
    item,
    selectedValueFormatter,
  ]);

  return (
    <Tooltip
      key={id}
      text={tooltipContent || defaultTooltip || ''}
      hideTooltip={!selected && !tooltipContent && !defaultTooltip}
    >
      {itemComponent}
    </Tooltip>
  );
};
